<script>
import General from '@/services/General'
import Customers from '@/services/Customers'
import Notification from '@/services/Notification'
import validationMessages from '@/components/validations'
import { validationMixin } from 'vuelidate'
import { required, requiredIf } from "vuelidate/lib/validators"
import moment from "moment"
import flatPickr from "vue-flatpickr-component"
import vue2Dropzone from "vue2-dropzone"

import "flatpickr/dist/flatpickr.css"
import "vue2-dropzone/dist/vue2Dropzone.min.css"

export default {
  mixins: [validationMixin],
  components: {
    validationMessages,
    flatPickr,
    vueDropzone: vue2Dropzone,
  },
  props: {
    groupOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const defaultStartDate = moment().format("YYYY-MM-DD")
    const defaultEndDate = moment().add(7, 'days').format("YYYY-MM-DD")

    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      acceptedFileTypeErrorMessage: '',
      acceptedFileTypeError: false,
      dateRangeError: '',
      additionalOptIns:[],
      showAdditionalOptInFields:false,
      emptyOptInField: false,
      actionTextOption: [],
      recipientOptions: [
        { value: 'ALL', text: 'All Users' },
        { value: 'IOS', text: 'IOS Users' },
        { value: 'ANDROID', text: 'Android Users' },
        { value: 'GROUP', text: 'Group' },
      ],
      systemGroups: [],
      userCreatedGroups: [],
      selectedSystemGroupId: '',
      selectedUserGroup: null,
      userGroupSearch: '',
      estimatedAudienceCount: null,
      estimatedAudienceLoading: false,
      estimatedAudienceRequestId: 0,
      popup: {
        campaign_name: '',
        recipient: 'ALL',
        group: '',
        popup_title: '',
        popup_description: '',
        opt_in_text: '',
        terms_url: '',
        image_file: null,
        success_title: '',
        success_description: '',
        button_cta_text: '',
        button_cta_url: '',
        action_text: '',
      },
      duration: {
        start_date: defaultStartDate,
        start_time: '00:00',
        end_date: defaultEndDate,
        end_time: '23:59',
      },
      startDateConfig: {
        dateFormat: "Y-m-d",
        minDate: "today",
      },
      endDateConfig: {
        dateFormat: "Y-m-d",
        minDate: "today",
      },
      timePickerConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        minuteIncrement: 1,
        allowInput:true,
        allowInvalidPreload: false,
        clickOpens: true,
      },
      dropzoneOptions: {
        url: '/',
        autoProcessQueue: false,
        addRemoveLinks: false,
        thumbnailWidth: 150,
        thumbnailMethod: 'contain',
        maxFilesize: 1,
        maxFiles: 1,
        acceptedFiles: 'image/jpeg,image/png',
        dictMaxFilesExceeded: 'You can only upload one image',
        dictFileTooBig: 'Image is larger than 1MB',
        uploadMultiple: false,
      },
      csrf_token: localStorage.getItem('csrf_token'),
      previewImageObjectUrl: null,
      appButtonColor: '#0b1120',
      appButtonTextColor: '#c4b5fd',
      /** Shared placeholders — used in inputs and preview when fields are empty */
      fieldPlaceholders: {
        campaign_name: 'Enter campaign name (internal use only)',
        popup_title: 'e.g., Win VIP Tickets',
        popup_description: 'Enter a compelling description of the giveaway',
        action_text: 'Enter button action text',
        opt_in_text: 'e.g., I agree to participate and accept the terms and conditions',
        terms_url: 'https://example.com/terms',
        success_title: "e.g., You're In!",
        success_description: "e.g., Thank you for participating! We'll announce the winner soon.",
        button_cta_text: 'e.g., Join Giveaway, Enter Now',
        button_cta_url: 'https://example.com/landing-page',
      },
    };
  },
  validations: {
    popup: {
      campaign_name: { required },
      recipient: { required },
      group: {
        required: requiredIf(function () {
          return this.popup.recipient === 'GROUP'
        })
      },
      popup_title: { required },
      popup_description: { required },
      opt_in_text: { required },
      terms_url: { required },
      image_file: { required },
      success_title: { required },
      success_description: { required },
      button_cta_text: { required },
    },
    duration: {
      start_date: { required },
      start_time: { required },
      end_date: { required },
      end_time: { required },
    }
  },
  created() {
    this.getCustomersGroups()
  },
  computed: {
    hasGroupSelection() {
      return this.popup.recipient === 'GROUP'
    },
    filteredUserCreatedForSearch() {
      const q = (this.userGroupSearch || '').trim().toLowerCase()
      const list = this.userCreatedGroups
      if (!q) {
        return list
      }
      return list.filter((g) => (g.name || '').toLowerCase().includes(q))
    },
    estimatedAudienceDisplay() {
      if (this.estimatedAudienceCount == null || this.estimatedAudienceCount === '') {
        return '—'
      }
      const n = Number(this.estimatedAudienceCount)
      if (Number.isNaN(n)) {
        return '—'
      }
      return n.toLocaleString()
    },
    selectedSystemGroup() {
      if (!this.selectedSystemGroupId) {
        return null
      }
      return this.systemGroups.find((g) => g.group_id === this.selectedSystemGroupId) || null
    },
    activeAudienceLabel() {
      if (this.selectedSystemGroup) {
        return (this.selectedSystemGroup.name || '').trim()
      }
      if (this.selectedUserGroup) {
        return (this.selectedUserGroup.name || '').trim()
      }
      return ''
    },
    hasActiveAudience() {
      return !!(this.selectedSystemGroupId || this.selectedUserGroup)
    },
    formattedStartDateTime() {
      return this.combineDateTime(this.duration.start_date, this.duration.start_time)
    },
    formattedEndDateTime() {
      return this.combineDateTime(this.duration.end_date, this.duration.end_time)
    },
    previewSuccessTitle() {
      return this.previewField(this.popup.success_title, 'success_title')
    },
    previewContentTitle() {
      return this.previewField(this.popup.popup_title, 'popup_title')
    },
    previewSuccessDescription() {
      return this.previewField(this.popup.success_description, 'success_description')
    },
    previewContentDescription() {
      return this.previewField(this.popup.popup_description, 'popup_description')
    },
    previewContentOpt() {
      return this.previewField(this.popup.opt_in_text, 'opt_in_text')
    },
    previewOptInItems() {
      const items = [this.previewContentOpt]
      this.additionalOptIns.forEach((opt) => {
        const trimmed = (opt || '').trim()
        if (trimmed) {
          items.push(trimmed)
        }
      })
      return items
    },
    hasSuccessCtaButton() {
      return Boolean((this.popup.button_cta_url || '').trim())
    },
    /** Success CTA: same field as CTA; default matches product mock. */
    previewSuccessCtaLabel() {
      return this.previewField(this.popup.button_cta_text, 'button_cta_text')
    },
    previewActionText() {
      return this.previewField(this.popup.action_text, 'action_text')
    },
    previewSimpleSuccessBody() {
      return this.previewField(this.popup.success_description, 'success_description')
    },
  },
  methods: {
    previewField(value, placeholderKey) {
      const trimmed = (value || '').trim()
      if (trimmed) {
        return trimmed
      }
      const placeholder = this.fieldPlaceholders[placeholderKey] || ''
      return placeholder.replace(/^e\.g\.\s*,?\s*/i, '').trim()
    },
    toggleOptInFields(){
        this.showAdditionalOptInFields = true;        
        this.addOptInField();
    },
    addOptInField() {
      this.additionalOptIns.push("");
    },
    removeOptIn(index) {
      this.additionalOptIns.splice(index, 1);
    },
    getCustomersGroups() {
      Customers.getCustomersGroups()
        .then((response) => {
          const rows = response.data.data ? response.data.data : []
          this.systemGroups = rows.filter((g) => Number(g.active) === 1)
          this.userCreatedGroups = rows.filter((g) => g.type === 'static' && Number(g.active) === 1)
        })
        .catch(() => {
          this.systemGroups = []
          this.userCreatedGroups = []
        })
    },
    async fetchEstimatedAudience() {
      const recipient = this.popup.recipient || 'ALL'
      if (!recipient) {
        this.estimatedAudienceCount = null
        return
      }

      if (recipient === 'GROUP') {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          ''
        if (!group_id) {
          this.estimatedAudienceLoading = false
          this.estimatedAudienceCount = null
          return
        }
      }

      const requestId = ++this.estimatedAudienceRequestId
      this.estimatedAudienceLoading = true

      let filters = ''
      if (recipient === 'GROUP') {
        const group_id =
          this.selectedSystemGroupId ||
          (this.selectedUserGroup && this.selectedUserGroup.group_id) ||
          ''
        filters = `&group_id=${encodeURIComponent(group_id)}`
      }

      try {
        const response = await Notification.getAudience(recipient, filters)
        const total =
          response && response.data && typeof response.data.total !== 'undefined'
            ? response.data.total
            : null
        if (requestId === this.estimatedAudienceRequestId) {
          this.estimatedAudienceCount = total != null ? Number(total) : null
        }
      } catch (e) {
        if (requestId !== this.estimatedAudienceRequestId) {
          return
        }
        if (recipient === 'GROUP') {
          if (this.selectedSystemGroupId) {
            const g = this.systemGroups.find((x) => x.group_id === this.selectedSystemGroupId)
            this.estimatedAudienceCount = g && g.total_members != null ? g.total_members : null
            return
          }
          if (this.selectedUserGroup && this.selectedUserGroup.total_members != null) {
            this.estimatedAudienceCount = this.selectedUserGroup.total_members
            return
          }
        }
        this.estimatedAudienceCount = null
      } finally {
        if (requestId === this.estimatedAudienceRequestId) {
          this.estimatedAudienceLoading = false
        }
      }
    },
    onSystemGroupChange() {
      if (this.selectedSystemGroupId) {
        this.selectedUserGroup = null
        this.userGroupSearch = ''
        this.popup.group = this.selectedSystemGroupId
      } else {
        this.popup.group = this.selectedUserGroup ? this.selectedUserGroup.group_id : ''
      }
      this.fetchEstimatedAudience()
    },
    clearSystemGroupSelection() {
      this.selectedSystemGroupId = ''
      this.popup.group = this.selectedUserGroup ? this.selectedUserGroup.group_id : ''
      this.fetchEstimatedAudience()
    },
    pickUserCreatedGroup(group) {
      this.selectedUserGroup = { ...group }
      this.selectedSystemGroupId = ''
      this.userGroupSearch = ''
      this.popup.group = group.group_id
      this.fetchEstimatedAudience()
    },
    clearUserCreatedSelection() {
      this.selectedUserGroup = null
      this.userGroupSearch = ''
      this.popup.group = this.selectedSystemGroupId || ''
      this.fetchEstimatedAudience()
    },
    clearActiveAudience() {
      if (this.selectedSystemGroupId) {
        this.clearSystemGroupSelection()
      } else if (this.selectedUserGroup) {
        this.clearUserCreatedSelection()
      }
    },
    onRecipientChange() {
      if (this.popup.recipient !== 'GROUP') {
        this.popup.group = ''
        this.selectedSystemGroupId = ''
        this.selectedUserGroup = null
        this.userGroupSearch = ''
      }
      this.fetchEstimatedAudience()
    },
    refreshPopupData() {
      this.$emit('onRefresh')
    },
    refreshPreviewImageUrl(file) {
      if (this.previewImageObjectUrl) {
        URL.revokeObjectURL(this.previewImageObjectUrl)
        this.previewImageObjectUrl = null
      }
      if (file) {
        this.previewImageObjectUrl = URL.createObjectURL(file)
      }
    },
    combineDateTime(date, time) {
      if (!date || !time) {
        return ''
      }

      return moment(`${date} ${time}`, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm")
    },
    validateDateRange() {
      this.dateRangeError = ''
      const hasAllDateParts = [
        this.duration.start_date,
        this.duration.start_time,
        this.duration.end_date,
        this.duration.end_time,
      ].every(Boolean)

      if (!hasAllDateParts) {
        this.dateRangeError = 'Please choose both start and end date with time.'
        return false
      }

      if (!this.formattedStartDateTime || !this.formattedEndDateTime) {
        return false
      }

      if (moment(this.formattedEndDateTime).isBefore(moment(this.formattedStartDateTime))) {
        this.dateRangeError = 'End date must be after start date.'
        return false
      }

      return true
    },
    async loadAppButtonColors() {
      try {
        const colors = await General.getAppButtonColors();
        Object.assign(this, colors);
      } catch (error) {
        // keep defaults
      }
    },
    onModalShown() {
      this.getActionTextOptions()
      this.loadAppButtonColors()
      this.fetchEstimatedAudience()
    },
    async createHypePopUp() {
      if (this.acceptedFileTypeError) {
        return
      }


      for(let field of this.additionalOptIns){
          if(field == ''){
            this.emptyOptInField = true;
            return;
          }else{
            this.emptyOptInField = false
          }
      }

      this.tryingToSubmit = true
      this.submitted = true
      this.$v.$touch()

      const hasValidRange = this.validateDateRange()

      if (this.$v.$invalid || !hasValidRange) {
        if (process.env.NODE_ENV !== 'production') {
          const invalidFields = []

          const traverseValidationTree = (validationNode, path = []) => {
            if (!validationNode || typeof validationNode !== 'object') {
              return
            }

            if (validationNode.$params) {
              Object.keys(validationNode.$params).forEach((rule) => {
                if (validationNode.$dirty && !validationNode[rule]) {
                  const fieldPath = path.length ? path.join('.') : 'root'
                  invalidFields.push(`${fieldPath} (${rule})`)
                }
              })
            }

            Object.keys(validationNode)
              .filter((key) => !key.startsWith('$'))
              .forEach((key) => {
                traverseValidationTree(validationNode[key], [...path, key])
              })
          }

          traverseValidationTree(this.$v.popup, ['popup'])
          traverseValidationTree(this.$v.duration, ['duration'])

          if (!hasValidRange && !invalidFields.includes('duration (date_range)')) {
            invalidFields.push('duration (date_range)')
          }

          // eslint-disable-next-line no-console
          console.warn('Giveaway popup validation failed:', invalidFields)
        }

        this.tryingToSubmit = false
        return
      }

      // Set default action_text if empty based on popup type (participation)
      let actionTextToSend = this.popup.action_text
      if (!actionTextToSend || actionTextToSend.trim() === '') {
        if (this.actionTextOption.length > 0) {
          const defaultOption = this.actionTextOption[0]
          const participationOption = this.actionTextOption[2] || defaultOption
          actionTextToSend = participationOption
        }
      }

      const formData = new FormData()
      formData.append('name', this.popup.campaign_name)
      formData.append('title', this.popup.popup_title)
      formData.append('bottom_text', this.popup.popup_description)
      formData.append('participation_agreement_text', this.popup.opt_in_text)
      formData.append('participation_tnc_url', this.popup.terms_url)
      formData.append('button_cta_text', this.popup.button_cta_text)
      formData.append('action_text', actionTextToSend)
      formData.append('action', this.popup.button_cta_url || '')
      formData.append('success_title', this.popup.success_title)
      formData.append('success_description', this.popup.success_description)
      formData.append('type', 'participation')
  
      if (this.additionalOptIns.length === 0) {
        formData.append('additional_opt_ins', []);
      } else {
        this.additionalOptIns.forEach(opt => {
          formData.append('additional_opt_ins[]', opt);
        });
      }
      formData.append('date_from', this.formattedStartDateTime)
      formData.append('date_to', this.formattedEndDateTime)
      if (this.popup.image_file) {
        formData.append('file', this.popup.image_file)
      }
      formData.append('recipient', this.popup.recipient)
      formData.append('csrf_token', this.csrf_token)
      formData.append('page_popup_title', this.popup.success_title)
      formData.append('page_popup_description', this.popup.success_description)
      formData.append('button_landing_url', this.popup.button_cta_url || '')

      if (this.hasGroupSelection) {
        formData.append('group_id', this.popup.group)
      }
      try {
        const response = await General.createHypePopUp(formData)
        let createdPopup = response?.data?.data ?? null

        if (createdPopup && typeof createdPopup === 'string') {
          try {
            const detailsResponse = await General.getHypePopUpById({
              popup_id: createdPopup,
              type: 'participation',
            })
            createdPopup = detailsResponse?.data?.data ?? createdPopup
          } catch (fetchError) {
            console.error('Failed to fetch created giveaway popup details', fetchError)
          }
        }

        if (createdPopup) {
          const popupName =
            (typeof createdPopup === 'object' && createdPopup !== null && createdPopup.name) ||
            this.popup.campaign_name

          this.successmsg(`Giveaway popup "${popupName}" created!`)
          this.refreshPopupData()
          this.closeModal()
        } else {
          this.failedmsg('Failed to create giveaway popup.')
        }
      } catch (error) {
        const resData = error?.response?.data?.error ? error.response.data.error : 'Failed'
        const errorMsg = Array.isArray(resData) ? resData[0] : resData
        this.failedmsg(errorMsg)
      } finally {
        this.tryingToSubmit = false
      }
    },
    closeModal() {
      this.showModal = false
    },
    onModalHidden() {
      this.refreshPreviewImageUrl(null)
      this.resetProps()
    },
    resetProps() {
      const defaultStartDate = moment().format("YYYY-MM-DD")
      const defaultEndDate = moment().add(7, 'days').format("YYYY-MM-DD")

      this.submitted = false
      this.acceptedFileTypeErrorMessage = ''
      this.acceptedFileTypeError = false
      this.dateRangeError = ''
      this.additionalOptIns = []
      this.showAdditionalOptInFields = false
      this.selectedSystemGroupId = ''
      this.selectedUserGroup = null
      this.userGroupSearch = ''
      this.estimatedAudienceCount = null
      this.estimatedAudienceLoading = false
      this.estimatedAudienceRequestId = 0
      this.popup = {
        campaign_name: '',
        recipient: 'ALL',
        group: '',
        popup_title: '',
        popup_description: '',
        opt_in_text: '',
        terms_url: '',
        image_file: null,
        success_title: '',
        success_description: '',
        button_cta_text: '',
        button_cta_url: '',
        action_text: '',
      }
      this.duration = {
        start_date: defaultStartDate,
        start_time: '00:00',
        end_date: defaultEndDate,
        end_time: '23:59',
      }

      if (this.$refs.giveawayDropzone) {
        this.$refs.giveawayDropzone.removeAllFiles(true)
      }
    },
    selectFile(files) {
      setTimeout(() => {
        const dropzoneFiles = this.$refs.giveawayDropzone?.dropzone?.files || []
        const [file] = files
        if (!file) {
          return
        }

        if (file.size > 1048576) {
          this.acceptedFileTypeErrorMessage = 'Max file size is 1MB.'
          this.acceptedFileTypeError = true
        } else if (!['image/png', 'image/jpeg'].includes(file.type)) {
          this.acceptedFileTypeErrorMessage = 'Only PNG or JPG images are allowed.'
          this.acceptedFileTypeError = true
        } else {
          this.acceptedFileTypeErrorMessage = ''
          this.acceptedFileTypeError = false
        }

        if (dropzoneFiles.length > 1) {
          dropzoneFiles.forEach((value, index) => {
            if (index !== 0) {
              this.$refs.giveawayDropzone.removeFile(value)
            }
          })
        }

        this.popup.image_file = !this.acceptedFileTypeError ? dropzoneFiles[0] : null
      }, 200)
    },
    removeImage() {
      if (this.$refs.giveawayDropzone) {
        this.$refs.giveawayDropzone.removeAllFiles(true)
      }
      this.popup.image_file = null
    },
    getActionTextOptions() {
      General.getactionTextOptions()
        .then((response) => {
          this.actionTextOption = response.data.data ? response.data.data : []
          // Don't auto-populate on initial load - let user manually enter
        })
        .catch(() => {
          this.actionTextOption = []
        })
    },
    formatTermsUrl(){
      const value = this.popup.terms_url;
      if(!value){return}

      if(!value.startsWith('http')) {
        this.popup.terms_url = `https://${value}`;
      }
    },
    formatButtonLandingPageUrl(){
      const value = this.popup.button_cta_url;
      if(!value){return}

      if(!value.startsWith('http')) {
        this.popup.button_cta_url = `https://${value}`;
      }
    }
  },
  watch: {
    'popup.image_file'(file) {
      this.refreshPreviewImageUrl(file)
    },
  },
  beforeDestroy() {
    this.refreshPreviewImageUrl(null)
  },
};
</script>

<template>
  <b-modal
    id="add_new_giveaway_popup"
    size="xl"
    v-model="showModal"
    @hidden="onModalHidden"
    @shown="onModalShown"
    hide-footer
    centered
  >
    <template v-slot:modal-header>
      <div class="w-100">
        <h2>Create a Giveaway Popup</h2>
        <p>Set up a new giveaway campaign for your sports club members</p>
      </div>
      <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
    </template>

    <b-form @submit.prevent="createHypePopUp" class="marketing-modal__hero">
      <div class="row g-4 align-items-start hype-add-two-col">
        <div class="col-12 col-lg-8 hype-add-layout-main">

        <section class="marketing-card">
          <header class="marketing-card__header">
            <h3>Basic Information</h3>
            <p>Essential details about your giveaway campaign</p>
          </header>

          <b-form-group
            label="Campaign Name"
            label-for="giveaway-campaign-name"
            class="mb-4"
          >
            <b-form-input
              id="giveaway-campaign-name"
              v-model.trim="popup.campaign_name"
              :class="{ 'is-invalid': submitted && $v.popup.campaign_name.$error }"
              :placeholder="fieldPlaceholders.campaign_name"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Campaign Name'"
              :validationName="$v.popup.campaign_name"
            />
          </b-form-group>

          <div class="mb-1">
            <label class="form-label d-block">Campaign Duration</label>
          </div>

          <div class="row g-3 flex-wrap">
            <div class="col-12 col-lg-6">
              <div class="marketing-datetime">
                <span class="marketing-datetime__label">From</span>
                <div class="marketing-datetime__inputs">
                  <flat-pickr
                    v-model="duration.start_date"
                    :config="startDateConfig"
                    :class="['form-control', submitted && $v.duration.start_date.$error ? 'is-invalid' : '']"
                    placeholder="Pick a date"
                  />
                  <flat-pickr
                    v-model="duration.start_time"
                    :config="timePickerConfig"
                    :class="['form-control', submitted && $v.duration.start_time.$error ? 'is-invalid' : '']"
                  />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-6">
              <div class="marketing-datetime">
                <span class="marketing-datetime__label">To</span>
                <div class="marketing-datetime__inputs">
                  <flat-pickr
                    v-model="duration.end_date"
                    :config="endDateConfig"
                    :class="['form-control', submitted && $v.duration.end_date.$error ? 'is-invalid' : '']"
                    placeholder="Pick a date"
                  />
                  <flat-pickr
                    v-model="duration.end_time"
                    :config="timePickerConfig"
                    :class="['form-control', submitted && $v.duration.end_time.$error ? 'is-invalid' : '']"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="dateRangeError" class="text-danger mt-2">
            {{ dateRangeError }}
          </div>
        </section>

        <div class="card hype-audience-card rounded-3 mb-4">
          <div class="card-body p-4">
            <h6 class="fw-semibold mb-1">Audience Targeting</h6>
            <p class="text-muted small mb-3">Select one audience group for this popup</p>

            <div class="mb-3">
              <label class="form-label small fw-medium mb-2">Recipients</label>
              <select
                v-model="popup.recipient"
                class="form-control form-select hype-audience-field hype-audience-flow-select"
                :class="{ 'is-invalid': submitted && $v.popup.recipient.$error }"
                @change="onRecipientChange"
              >
                <option
                  v-for="opt in recipientOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.text }}
                </option>
              </select>
              <validationMessages
                v-if="submitted"
                :fieldName="'Target audience'"
                :validationName="$v.popup.recipient"
              />
            </div>

            <div v-if="hasGroupSelection">
              <label class="form-label small fw-medium mb-2">Audience Group</label>

              <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">System groups</p>
              <select
                id="giveaway-audience-system-group"
                v-model="selectedSystemGroupId"
                class="form-select form-control hype-audience-field hype-audience-flow-select audience-system-select mb-3"
                :disabled="!!selectedUserGroup"
                @change="onSystemGroupChange"
              >
                <option value="">Select a system group</option>
                <option
                  v-for="g in systemGroups"
                  :key="g.group_id"
                  :value="g.group_id"
                >
                  {{ g.name }}
                </option>
              </select>

              <div class="audience-or-divider" aria-hidden="true">
                <span></span>
                <strong>OR</strong>
                <span></span>
              </div>

              <p class="text-muted text-uppercase small fw-semibold mb-2 audience-sub-label">
                User created groups
              </p>
              <div class="audience-user-block">
                <div
                  class="audience-user-search-wrap"
                  :class="{ 'audience-user-search-wrap--muted': !!selectedSystemGroupId }"
                >
                  <div class="position-relative">
                    <i
                      class="uil uil-search position-absolute text-muted audience-search-icon"
                      aria-hidden="true"
                    ></i>
                    <input
                      v-model="userGroupSearch"
                      type="text"
                      class="form-control rounded-3 hype-audience-field ps-5 audience-user-search-input"
                      placeholder="Search user created groups..."
                      autocomplete="off"
                      :disabled="!!selectedSystemGroupId"
                    />
                    <ul
                      v-if="filteredUserCreatedForSearch.length > 0 && userGroupSearch.trim() && !selectedSystemGroupId"
                      class="list-group position-absolute w-100 shadow-sm mt-1 audience-suggest-list"
                    >
                      <li
                        v-for="g in filteredUserCreatedForSearch"
                        :key="g.group_id"
                        class="list-group-item list-group-item-action small py-2 cursor-pointer"
                        role="button"
                        @click="pickUserCreatedGroup(g)"
                      >
                        {{ g.name }}
                      </li>
                    </ul>
                  </div>
                </div>

                <div
                  v-if="hasActiveAudience && activeAudienceLabel"
                  class="audience-active-pill d-flex align-items-center justify-content-between gap-2 mt-3"
                >
                  <span class="d-flex align-items-center min-w-0 flex-grow-1">
                    <span class="audience-active-dot flex-shrink-0" aria-hidden="true"></span>
                    <span class="audience-active-name small fw-semibold text-truncate">{{ activeAudienceLabel }}</span>
                  </span>
                  <button
                    type="button"
                    class="audience-active-clear flex-shrink-0"
                    aria-label="Clear audience group"
                    @click="clearActiveAudience"
                  >
                    ×
                  </button>
                </div>
              </div>

              <validationMessages
                v-if="submitted && hasGroupSelection"
                :fieldName="'User segment'"
                :validationName="$v.popup.group"
              />
            </div>

            <div class="border rounded-3 p-3 mt-4 audience-estimated-box">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="fw-semibold small">Estimated Audience</span>
              </div>
              <p class="text-muted extra-small mb-2 audience-estimated-footnote">
                * Estimates are for users with the app installed
              </p>
              <div class="text-center fs-1 fw-bold py-1 audience-estimated-value">
                <span v-if="estimatedAudienceLoading" class="d-inline-flex align-items-center justify-content-center gap-2">
                  <b-spinner class="align-middle"></b-spinner>
                </span>
                <span v-else>{{ estimatedAudienceDisplay }}</span>
              </div>
            </div>
          </div>
        </div>

        <section class="marketing-card">
          <header class="marketing-card__header">
            <h3>Popup Content</h3>
            <p>Configure what users see in the giveaway popup</p>
          </header>

          <b-form-group
            label="Popup Title"
            class="mb-4"
          >
            <b-form-input
              v-model.trim="popup.popup_title"
              :placeholder="fieldPlaceholders.popup_title"
              :class="{ 'is-invalid': submitted && $v.popup.popup_title.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Popup title'"
              :validationName="$v.popup.popup_title"
            />
          </b-form-group>

          <b-form-group
            label="Popup description *"
            class="mb-4"
          >
            <b-form-textarea
              v-model.trim="popup.popup_description"
              :placeholder="fieldPlaceholders.popup_description"
              rows="3"
              :class="{ 'is-invalid': submitted && $v.popup.popup_description.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Popup description'"
              :validationName="$v.popup.popup_description"
            />
          </b-form-group>
          <b-form-group
            label="Button action text"
            label-for="button-action-text"
            class="mb-4"
          >
            <b-form-input
              id="button-action-text"
              v-model.trim="popup.action_text"
              :placeholder="fieldPlaceholders.action_text"
            />
           
          </b-form-group>
          <div class="mb-4">
            <div class="d-flex align-items-center justify-content-between">
              <label style="font-weight:400;">Opt-in text *</label>
              <button @click="toggleOptInFields" type="button" class="btn btn-outline-secondary mb-3">Add Additional</button>
            </div>

       <b-form-group
          >
            <b-form-input
              v-model.trim="popup.opt_in_text"
              :placeholder="fieldPlaceholders.opt_in_text"
              :class="{ 'is-invalid': submitted && $v.popup.opt_in_text.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Opt-in text'"
              :validationName="$v.popup.opt_in_text"
            />
          </b-form-group>

                 <div v-for="(optInFiled, index) in additionalOptIns" :key="index" class="mb-1 mt-3 border rounded-3">
                <div class="d-flex align-items-start gap-2"  v-if="showAdditionalOptInFields">
                  <b-form-input
                      v-model.trim="additionalOptIns[index]"
                      type="text"
                      :id="'optin-' + index"
                      :class="{ 'is-invalid': additionalOptIns[index].trim() === ''  }"
                  >
                  </b-form-input>
                  <b-button
                      variant="outline-danger"
                      class="ms-2"
                      @click.prevent="removeOptIn(index)"
                      title="Remove this answer">
                    <i class="uil uil-trash-alt"></i>
                  </b-button>
                </div>
              </div>
              <span v-if="emptyOptInField" class="text-danger font-size-12 ms-1">Opt-In Text can't be empty</span>


          </div>
   

          <b-form-group
            label="Terms & Conditions URL"
            class="mb-0"
          >
            <b-form-input
              v-model.trim="popup.terms_url"
              :placeholder="fieldPlaceholders.terms_url"
              @blur="formatTermsUrl"
              :class="{ 'is-invalid': submitted && $v.popup.terms_url.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Terms & Conditions URL'"
              :validationName="$v.popup.terms_url"
            />
          </b-form-group>
        </section>

        <section class="marketing-card">
          <header class="marketing-card__header marketing-card__header--icon">
            <i class="uil uil-image"></i>
            <div>
              <h3>Popup Image</h3>
              <p>Recommended: 955x425 pixels.</p>
            </div>
          </header>

          <div
            :class="[
              'marketing-upload',
              submitted && $v.popup.image_file.$error ? 'is-invalid' : '',
              acceptedFileTypeError ? 'has-error' : ''
            ]"
          >
            <vue-dropzone
              id="giveaway-dropzone"
              ref="giveawayDropzone"
              :options="dropzoneOptions"
              :use-custom-slot="true"
              @vdropzone-files-added="selectFile"
              no-progress-bar
            >
              <div class="marketing-upload__placeholder">
                <i class="uil uil-import"></i>
                <span>Drag and drop an image, or click to browse</span>
                <small>PNG, JPG up to 4MB</small>
              </div>
            </vue-dropzone>
          </div>

          <div class="d-flex align-items-center gap-3 mt-3" v-if="popup.image_file">
            <span class="text-muted small">{{ popup.image_file.name }}</span>
            <b-button size="sm" variant="outline-danger" @click="removeImage">Remove</b-button>
          </div>

          <validationMessages
            v-if="submitted"
            :fieldName="'Popup image'"
            :validationName="$v.popup.image_file"
          />
          <p v-if="acceptedFileTypeError" class="text-danger mt-2">{{ acceptedFileTypeErrorMessage }}</p>
        </section>

        <section class="marketing-card">
          <header class="marketing-card__header">
            <h3>Success Page Content</h3>
            <p>Configure what users see after joining the giveaway</p>
          </header>

          <b-form-group
            label="Success Page Popup Title"
            class="mb-4"
          >
            <b-form-input
              v-model.trim="popup.success_title"
              :placeholder="fieldPlaceholders.success_title"
              :class="{ 'is-invalid': submitted && $v.popup.success_title.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Success page popup title'"
              :validationName="$v.popup.success_title"
            />
          </b-form-group>

          <b-form-group
            label="Success Page Popup Description"
            class="mb-4"
          >
            <b-form-textarea
              v-model.trim="popup.success_description"
              :placeholder="fieldPlaceholders.success_description"
              rows="3"
              :class="{ 'is-invalid': submitted && $v.popup.success_description.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Success page popup description'"
              :validationName="$v.popup.success_description"
            />
          </b-form-group>

          <b-form-group
            label="Button CTA Text"
            class="mb-4"
          >
            <b-form-input
              v-model.trim="popup.button_cta_text"
              :placeholder="fieldPlaceholders.button_cta_text"
              :class="{ 'is-invalid': submitted && $v.popup.button_cta_text.$error }"
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Button CTA text'"
              :validationName="$v.popup.button_cta_text"
            />
          </b-form-group>

          <b-form-group
            label="Button Landing Page URL"
            class="mb-0"
          >
            <b-form-input
              v-model.trim="popup.button_cta_url"
              :placeholder="fieldPlaceholders.button_cta_url"
              @blur="formatButtonLandingPageUrl"
            />
          </b-form-group>
        </section>

        <input type="hidden" name="csrf_token" v-model="csrf_token">

        <footer class="marketing-modal__footer">
          <b-button variant="outline-secondary" @click="closeModal">Cancel</b-button>
          <b-button variant="dark" type="submit" :disabled="tryingToSubmit">
            <b-spinner v-if="tryingToSubmit" small class="me-2" />
            Create Popup
          </b-button>
        </footer>

        </div>

        <div class="col-12 col-lg-4 hype-add-preview-sticky">
          <div class="marketing-card hype-add-preview-card mb-3 mb-lg-0">
            <header class="marketing-card__header">
              <h3>Popup Preview</h3>
              <p>How your giveaway success screens look with app button styling</p>
            </header>

            <div class="giveaway-preview-panel">
              <!--
                Success: two reference UIs (assignment / product):
                (A) simple — title "Success", check icon, one button "OKAY"
                (B) giveaway — hero image, copy, "Can't wait?", CTA e.g. BUY NOW
              -->
              <div class="w-100">
                <h5 class="text-muted small fw-semibold mb-1 d-flex align-items-center gap-2">
                  <i class="uil uil-check-circle text-success font-size-16"></i>
                  Success page (after opt-in)
                </h5>
                <p class="text-muted font-size-12 mb-3">
                  Two layouts match the two handouts: a minimal confirmation, then the giveaway / shop follow-up. Both use your success page fields where it applies.
                </p>

                <p class="giveaway-preview-ref-label">giveaway with image &amp; shop CTA</p>

                <div class="giveaway-preview-success-stack mb-3">
                  <div class="giveaway-preview-success giveaway-preview-success--framed">
                      <div class="giveaway-preview-success__image">
                        <span
                          class="giveaway-preview-success__close"
                          aria-hidden="true"
                          title="Close (preview only)"
                        >
                          <i class="uil uil-times"></i>
                        </span>
                        <img
                          v-if="previewImageObjectUrl"
                          :src="previewImageObjectUrl"
                          alt=""
                          class="giveaway-preview-success__img"
                        />
                        <div
                          v-else
                          class="giveaway-preview-success__img-placeholder d-flex flex-column align-items-center justify-content-center w-100 h-100"
                        >
                          <i class="uil uil-image" style="font-size: 40px; color: #9ca3af;"></i>
                        </div>
                      </div>
                      <div class="giveaway-preview-success__body text-center">
                        <h6 class="giveaway-preview-success__title mb-2">
                          {{ previewContentTitle }}
                        </h6>
                        <p class="giveaway-preview-success__desc mb-2">
                          {{ previewContentDescription }}
                        </p>
                        <p
                          v-for="(optLine, optIndex) in previewOptInItems"
                          :key="`preview-opt-in-${optIndex}`"
                          class="giveaway-preview-success__teaser small"
                          :class="optIndex === previewOptInItems.length - 1 ? 'mb-3' : 'mb-1'"
                        >
                          {{ optLine }}
                        </p>
                        <button
                          type="button"
                          class="giveaway-preview-success__btn"
                          :class="{ 'is-disabled': !hasSuccessCtaButton }"
                          :style="{ backgroundColor: appButtonColor, color: appButtonTextColor }"
                          :title="!hasSuccessCtaButton ? 'Add a button landing page URL in the form to show this in the app' : ''"
                          disabled
                        >
                          {{previewActionText}}
                        </button>
                      </div>
                    </div>
                </div>
                <p class="giveaway-preview-ref-label">simple confirmation</p>
                <div class="giveaway-preview-success-stack mb-4">
                    <div class="giveaway-preview-success giveaway-preview-success--simple giveaway-preview-success--framed">
                      <span
                        class="giveaway-preview-success__close"
                        aria-hidden="true"
                      >
                        <i class="uil uil-times"></i>
                      </span>
                      <div class="giveaway-preview-success__body giveaway-preview-success__body--simple text-center">
                        <h6 class="giveaway-preview-success__kicker mb-3">
                          {{ previewSuccessTitle }}
                        </h6>
                        <div class="giveaway-preview-success__ok-bubble mb-3">
                          <i class="uil uil-check"></i>
                        </div>
                        <p class="giveaway-preview-success__desc giveaway-preview-success__desc--simple mb-3">
                          {{ previewSimpleSuccessBody }}
                        </p>
                        <button
                          type="button"
                          class="giveaway-preview-success__btn"
                          :style="{ backgroundColor: appButtonColor, color: appButtonTextColor }"
                          disabled
                          title="Preview only; label can match your CTA in the app"
                        >
                        {{ previewSuccessCtaLabel }}
                        </button>
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-form>
  </b-modal>
</template>

<style scoped>
.marketing-modal__hero {
  margin-bottom: 24px;
}

.marketing-card {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 12px 32px rgb(15 23 42 / 6%);
}

.marketing-card__header {
  margin-bottom: 24px;
}

.marketing-card__header h3 {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: #151a2d;
}

.marketing-card__header p {
  margin: 0;
  color: #5f6b7d;
}

.marketing-card__header--icon {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.marketing-card__header--icon i {
  font-size: 32px;
  color: #151a2d;
  line-height: 1;
}

.marketing-datetime {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.marketing-datetime__label {
  font-weight: 600;
  color: #151a2d;
}

.marketing-datetime__inputs {
  display: flex;
  gap: 12px;
  align-items: center;
}

.marketing-datetime__inputs .form-control {
  padding: 12px 16px;
  border-radius: 12px;
  background: #f1f3f7;
  border: 1px solid transparent;
}

.marketing-datetime__inputs .form-control:focus {
  border-color: #6366f1;
  background: #ffffff;
  box-shadow: none;
}

.marketing-upload {
  border: 2px dashed #d3d8e6;
  border-radius: 16px;
  background: #f7f8fb;
  padding: 32px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.marketing-upload.is-invalid {
  border-color: #f97373;
}

.marketing-upload.has-error {
  border-color: #f97373;
  background: #fff5f5;
}

.marketing-upload__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #5f6b7d;
  font-size: 14px;
  text-align: center;
}

.marketing-upload__placeholder i {
  font-size: 32px;
  color: #151a2d;
}

.marketing-upload__placeholder small {
  color: #a0aec0;
}

.marketing-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.hype-add-preview-sticky {
  position: sticky;
  top: 1rem;
  z-index: 2;
  align-self: flex-start;
}

.giveaway-preview-panel {
  width: 100%;
}

/* Audience targeting — matches modalAddHypePopUp */
.hype-audience-card {
  border: 1px solid #e5e5e5;
  background-color: #fff;
  box-shadow: none;
}

.hype-audience-card .extra-small {
  font-size: 0.7rem;
}

.hype-audience-card select.hype-audience-field,
.hype-audience-card input.form-control.hype-audience-field,
.hype-audience-card textarea.form-control.hype-audience-field {
  background-color: #f3f3f5 !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
}

.hype-audience-card select.hype-audience-field,
.hype-audience-card input.form-control.hype-audience-field {
  min-height: 42px;
}

.hype-audience-card select.hype-audience-field:focus,
.hype-audience-card input.form-control.hype-audience-field:focus,
.hype-audience-card textarea.form-control.hype-audience-field:focus {
  background-color: #f3f3f5 !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  box-shadow: none !important;
}

.hype-audience-card .form-select {
  border-radius: 10px;
}

.audience-sub-label {
  letter-spacing: 0.03em;
  font-size: 0.7rem;
}

.audience-search-icon {
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.audience-suggest-list {
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 0.375rem;
}

.audience-estimated-box {
  background-color: #f3f3f5;
}

.cursor-pointer {
  cursor: pointer;
}

.audience-user-search-wrap--muted .audience-user-search-input {
  opacity: 0.75;
  cursor: not-allowed !important;
}

.audience-or-divider {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 1rem;
  color: #6c757d;
}

.audience-or-divider span {
  height: 1px;
  background: #dee2e6;
}

.audience-or-divider strong {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.audience-active-pill {
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #b6d4fe;
  background-color: #e8f2fc;
}

.audience-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0d6efd;
  margin-right: 0.65rem;
}

.audience-active-name {
  color: #212529;
}

.audience-active-clear {
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  margin: 0;
  line-height: 1;
  font-size: 1.35rem;
  font-weight: 400;
  color: #0d6efd;
  cursor: pointer;
  border-radius: 6px;
}

.audience-active-clear:hover {
  color: #0a58ca;
  background-color: rgba(13, 110, 253, 0.1);
}

.audience-estimated-footnote {
  line-height: 1.35;
}

/* Success page modal (reference: post–opt-in full-screen) */
.giveaway-preview-ref-label {
  text-align: center;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

/* Success page previews — card only (no faux phone bezel) */
.giveaway-preview-success-stack {
  width: 100%;
  max-width: 310px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.giveaway-preview-success {
  width: 100%;
  max-width: 280px;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
}

.giveaway-preview-success--simple {
  overflow: visible;
}

.giveaway-preview-success__image {
  position: relative;
  height: 118px;
  background: #d1d5db;
  overflow: hidden;
}

.giveaway-preview-success__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.giveaway-preview-success__img-placeholder {
  min-height: 118px;
  background: linear-gradient(180deg, #e5e7eb 0%, #d1d5db 100%);
  color: #9ca3af;
}

.giveaway-preview-success__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  color: #374151;
  font-size: 16px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  cursor: default;
  pointer-events: none;
}

.giveaway-preview-success__body {
  padding: 16px 16px 18px;
  color: #111827;
}

.giveaway-preview-success__body--simple {
  padding: 14px 18px 20px;
  padding-top: 40px;
}

.giveaway-preview-success__kicker {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
}

.giveaway-preview-success__ok-bubble {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6ee7b7;
  color: #fff;
  font-size: 26px;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
}

.giveaway-preview-success__desc--simple {
  font-size: 11px;
  line-height: 1.5;
  color: #1f2937;
  max-width: 240px;
  margin-left: auto;
  margin-right: auto;
}

.giveaway-preview-success__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
  color: #111827;
}

.giveaway-preview-success__desc {
  font-size: 12px;
  line-height: 1.45;
  margin: 0;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
}

.giveaway-preview-success__teaser {
  color: #111827;
  font-weight: 500;
  margin: 0;
}

.giveaway-preview-success__btn {
  width: 100%;
  border: none;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: none;
  line-height: 1.2;
  cursor: default;
}

/* .giveaway-preview-success__btn.is-disabled {
  opacity: 0.5;
  filter: grayscale(0.1);
} */

@media (max-width: 767.98px) {
  .marketing-card {
    padding: 20px;
  }

  .marketing-datetime__inputs {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>